import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <section className="pt-28 pb-16 lg:pt-36 lg:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4" data-testid="privacy-heading">
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-500 mb-12">Last updated: February 1, 2026</p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">1. Introduction</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                Crystal Intelligence ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this policy carefully. By using our services, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">2. Information We Collect</h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">We may collect information about you in a variety of ways, including:</p>
              <ul className="space-y-2">
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-white">Personal Data:</strong> Name, email address, phone number, and other contact information you provide when creating an account, submitting a case report, or contacting us.</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-white">Financial Data:</strong> Payment card information, cryptocurrency wallet addresses, and transaction details necessary to provide our services.</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-white">Usage Data:</strong> Information about how you access and use our website and services, including IP address, browser type, pages visited, and time spent on our platform.</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-white">Case Data:</strong> Information related to your investigation case, including blockchain transaction details, wallet addresses, and supporting documentation.</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">3. How We Use Your Information</h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">We use the information we collect to:</p>
              <ul className="space-y-2">
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Provide, operate, and maintain our services</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Process and manage your account, transactions, and case reports</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Conduct blockchain investigations and asset tracing as requested</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Communicate with you about your account, cases, and our services</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Comply with legal obligations and respond to lawful requests</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Improve our platform and develop new features</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">4. Data Security</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and access controls. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">5. Data Sharing and Disclosure</h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">We may share your information in the following circumstances:</p>
              <ul className="space-y-2">
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-white">Law Enforcement:</strong> When required by law or to cooperate with legal proceedings and investigations.</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-white">Service Providers:</strong> With trusted third-party service providers who assist in operating our platform.</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong className="text-white">With Your Consent:</strong> When you have given us explicit permission to share your information.</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">6. Data Retention</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law. Case-related data may be retained for extended periods to support ongoing or future investigations.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">7. Your Rights</h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">Depending on your jurisdiction, you may have the following rights:</p>
              <ul className="space-y-2">
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Access to your personal data</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Correction of inaccurate or incomplete data</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Deletion of your personal data</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Restriction of processing of your data</span>
                </li>
                <li className="text-sm text-slate-400 leading-relaxed flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 mr-3 flex-shrink-0"></span>
                  <span>Data portability</span>
                </li>
              </ul>
              <p className="text-sm text-slate-400 leading-relaxed mt-3">To exercise any of these rights, please contact us at privacy@crystallintelligence.com.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">8. Cookies and Tracking</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                We may use cookies and similar tracking technologies to enhance your experience on our website. You can control cookies through your browser settings. Essential cookies required for the operation of our platform cannot be disabled.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-medium text-white mb-3">9. Contact Us</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:<br />
                <strong className="text-white">Email:</strong> privacy@crystallintelligence.com<br />
                <strong className="text-white">Address:</strong><br />
                1211 SE 5th Avenue<br />
                Crystal River, FL 34429
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
